import { get_token } from "@/router";
import {
  get_active_organization_id,
  get_active_profile_id,
  get_organization_branding,
  get_organization_profile_branding,
} from "@/utils/organization_profile";
import { request_organization_members } from "./organization_members_api";
import {
  first_array,
  normalize_member,
  parse_json_or_null,
} from "./organization_members_helpers";

export default {
  get_token() {
    return get_token();
  },
  set_active_profile(profile) {
    this.active_profile = profile || null;
    this.saved_branding = this.get_saved_branding();
    this.fetch_organization_members();
  },
  get_active_profile_id() {
    return get_active_profile_id(this.active_profile || {});
  },
  get_company_id() {
    return (
      get_active_organization_id() ||
      null
    );
  },
  get_branding_context() {
    return ({
      organization_id: get_active_organization_id(),
      profile_id: this.get_active_profile_id(),
      user_id: this.user_details?.user_id,
    });
  },
  get_saved_branding() {
    const profile_id = this.get_active_profile_id() || this.user_details?.user_id || "current";
    return [
      this.get_context_branding(),
      this.get_profile_branding(),
      this.get_stored_branding(profile_id),
    ].find(Boolean) || null;
  },
  get_context_branding() {
    return get_organization_branding(this.get_branding_context());
  },
  get_profile_branding() {
    return get_organization_profile_branding(
      this.active_profile?.company || this.active_profile || {},
      this.user_details || {}
    );
  },
  get_stored_branding(profile_id) {
    return parse_json_or_null(localStorage.getItem(`organization_branding_${profile_id}`));
  },
  get_member_payload(extra = {}) {
    const payload = {
      organization_id: get_active_organization_id(),
    };
    if (!payload.organization_id) {
      payload.profile_id = this.get_active_profile_id();
    }
    return Object.assign(payload, extra);
  },
  normalize_query(value) {
    return String(value || "").trim().toLowerCase();
  },
  user_matches_query(user, query) {
    return [
      user?.first_name,
      user?.last_name,
      user?.email,
      user?.full_name,
      user?.user?.first_name,
      user?.user?.last_name,
      user?.user?.email,
    ].some((value) => String(value || "").toLowerCase().includes(query));
  },
  get_members_from_response(response) {
    const members = first_array([
      response?.data?.members,
      response?.data?.organization_members,
      response?.members,
      response?.organization_members,
      response?.data,
    ]);
    return Array.isArray(members) ? members.map(normalize_member) : [];
  },
  has_member_lookup_context() {
    return !!this.get_token() && !!(get_active_organization_id() );
  },
  has_members_response(response) {
    return !!(
      response?.status === "success" ||
      response?.data ||
      response?.members ||
      response?.organization_members
    );
  },
  async fetch_organization_members() {
    if (!this.has_member_lookup_context()) return;
    this.destroy_members_datatable();
    this.is_loading_members = true;
    try {
      const response = await request_organization_members(this.get_member_payload());
      if (this.has_members_response(response)) {
        this.organization_members = this.get_members_from_response(response);
      }
    } catch {
      this.show_error("Could not load organisation members");
    } finally {
      this.is_loading_members = false;
      this.refresh_members_datatable();
    }
  },
  refresh_members_datatable() {
    this.$nextTick(() => this.init_members_datatable());
  },
  has_datatable_plugin() {
    return typeof $ !== "undefined" && !!($.fn?.DataTable || $.fn?.dataTable);
  },
  is_members_datatable_initialized(table) {
    if (!this.has_datatable_plugin()) return false;
    if ($.fn.dataTable?.isDataTable) return $.fn.dataTable.isDataTable(table);
    if ($.fn.DataTable?.isDataTable) return $.fn.DataTable.isDataTable(table);
    return false;
  },
  get_members_table() {
    return this.$refs.organizationMembersTable;
  },
  can_init_members_datatable(table) {
    if (!table) return false;
    if (!this.has_datatable_plugin()) return false;
    return !this.is_members_datatable_initialized(table);
  },
  get_members_datatable_column_defs() {
    return this.can_manage_members
      ? [{ orderable: false, searchable: false, targets: -1 }]
      : [];
  },
  get_members_datatable_options() {
    return ({
      pageLength: 10,
      ordering: false,
      autoWidth: false,
      scrollX: true,
      dom: "lfrtip",
      search: {
        caseInsensitive: true,
        smart: true,
      },
      columnDefs: this.get_members_datatable_column_defs(),
      language: {
        emptyTable: "No members found",
        info: "Showing _START_ to _END_ of _TOTAL_ members",
        infoEmpty: "Showing 0 members",
        zeroRecords: "No matching members found",
        paginate: {
          previous: "Prev",
          next: "Next",
        },
      },
    });
  },
  init_members_datatable() {
    const table = this.get_members_table();
    if (!this.can_init_members_datatable(table)) return;

    this.members_table_instance = $(table).DataTable(this.get_members_datatable_options());
    this.search_members_table(this.search_query);
  },
  destroy_members_datatable() {
    const table = this.$refs.organizationMembersTable;
    if (!table || !this.has_datatable_plugin()) return;

    if (this.is_members_datatable_initialized(table)) {
      $(table).DataTable().clear().destroy();
    }
    this.members_table_instance = null;
  },
  sync_members_table_instance() {
    const table = this.get_members_table();
    if (!table) return;
    if (!this.is_members_datatable_initialized(table)) return;
    this.members_table_instance = $(table).DataTable();
  },
  get_members_table_search_value(value) {
    return String(value || "").trim();
  },
  search_members_table(value) {
    if (!this.members_table_instance) this.sync_members_table_instance();
    if (!this.members_table_instance) return;
    this.members_table_instance.search(this.get_members_table_search_value(value)).draw();
  },
  clear_member_search() {
    this.search_query = "";
  },
};
